import React, { useState, useEffect } from 'react';
import { RootState } from '../Redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { setAuth } from '../Redux/authSlice';
import { translations } from '../utils/translations';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Modal, Button, Card, Breadcrumb, Pagination } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faEye, faStreetView, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import "./products.css";

interface Category {
    id: number;
    name: string;
}

interface Supplier {
    id: number;
    name: string;
}

interface Product {
    id: number;
    name: string;
    supplier_name: string;
    description: string;
    price: string;
    cost_price: string;
    quantity_in_stock: number;
    barcode: string;
    unit_of_measurement: string;
    category_name: string;
    status: string;
    date_added: string;
    image: null;
}

const lowThereshold = 5;

export default function ProductComponent() {
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [userRole, setUserRole] = useState<string | null>(null);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
    const [showEditModal, setShowEditModal] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [searchTerm, setSearchTerm] = useState<string>('');

    const [newProduct, setNewProduct] = useState<Partial<Product>>({
        name: '',
        description: '',
        price: '',
        cost_price: '',
        quantity_in_stock: 0,
        barcode: '',
        unit_of_measurement: '',
        category_name: '',
        supplier_name: '',
        status: 'A',
    });

    const [categories, setCategories] = useState<Category[]>([]);
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);

    const dispatch = useDispatch();
    const auth = useSelector((state: RootState) => state.auth.value);
    const language = useSelector((state: RootState) => state.language.language);
    const t = translations[language as keyof typeof translations];

    const getCookie = (name: string) => {
        const cookieName = name + "=";
        const decodedCookie = decodeURIComponent(document.cookie);
        const cookieArray = decodedCookie.split(';');
        for (let i = 0; i < cookieArray.length; i++) {
            let cookie = cookieArray[i];
            while (cookie.charAt(0) === ' ') {
                cookie = cookie.substring(1);
            }
            if (cookie.indexOf(cookieName) === 0) {
                return cookie.substring(cookieName.length, cookie.length);
            }
        }
        return '';
    };

    const fetchData = async () => {
        try {
            const token = getCookie('token');
            if (!token) {
                dispatch(setAuth(false));
                return;
            }
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            const userResponse = await axios.get('https://api.sominnovations.xyz/users/user', {
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            setUserRole(userResponse.data.user.role); // Set the user role
            dispatch(setAuth(true));
        } catch (e) {
            dispatch(setAuth(false));
        }
    };

    const fetchProducts = async (page: number) => {
        setLoading(true);
        try {
            const response = await axios.get(`https://api.sominnovations.xyz/Branches/products/?page=${page}&search=${searchTerm}`, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            setProducts(response.data.results);
            setTotalPages(Math.ceil(response.data.count / 20));
        } catch (error) {
            console.error('Error fetching products:', error);
        }
        setLoading(false);
    };

    const fetchCategoriesAndSuppliers = async () => {
        try {
            const categoriesResponse = await axios.get('https://api.sominnovations.xyz/Branches/categories/');
            setCategories(categoriesResponse.data.results);
            const suppliersResponse = await axios.get('https://api.sominnovations.xyz/Branches/suppliers/');
            setSuppliers(suppliersResponse.data.results);
        } catch (error) {
            console.error('Error fetching categories and suppliers:', error);
        }
    };

    useEffect(() => {
        fetchData();
    }, [dispatch]);

    useEffect(() => {
        if (auth) {
            fetchProducts(currentPage);
            fetchCategoriesAndSuppliers();
        }
    }, [auth, currentPage, searchTerm]);

    const handleView = (product: Product) => {
        setSelectedProduct(product);
        setShowDetailModal(true);
    };

    const handleEdit = (product: Product) => {
        setSelectedProduct(product);
        setShowEditModal(true);
    };

    const updateProduct = async () => {
        if (selectedProduct) {
            try {
                const updatedData = {
                    name: selectedProduct.name,
                    description: selectedProduct.description,
                    price: selectedProduct.price,
                    cost_price: selectedProduct.cost_price,
                    quantity_in_stock: selectedProduct.quantity_in_stock,
                };

                const response = await axios.patch(`https://api.sominnovations.xyz/Branches/products/${selectedProduct.id}/`, updatedData, {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                setShowEditModal(false);
                fetchProducts(currentPage);
            } catch (error) {
                console.error('Error updating product:', error);
            }
        }
    };

    const handleDelete = async (id: number) => {
        const confirmDelete = window.confirm('Are you sure you want to delete this product?');
        if (confirmDelete) {
            try {
                await axios.delete(`https://api.sominnovations.xyz/Branches/products/${id}/`);
                fetchProducts(currentPage);
            } catch (error) {
                console.error('Error deleting product:', error);
            }
        }
    };

    return (
        <div className="container mt-1 con-product" style={{ padding: "20px" }}>
            <Card className="mb-4">
                <Card.Header style={{ backgroundColor: '#5271ff', color: '#fff' }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <h4>{t.products}</h4>
                        <Breadcrumb style={{ color: "white" }}>
                            <Breadcrumb.Item href="dashboard"><p style={{ color: "white" }}>{t.dashboard}</p></Breadcrumb.Item>
                            <Breadcrumb.Item active style={{ color: "white" }}>{t.products}</Breadcrumb.Item>
                        </Breadcrumb>
                    </div>
                </Card.Header>
                <Card.Body>
                    <div className="mb-4">
                        <input
                            type="text"
                            className="form-control"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <Link to="/add-products" style={{ display: "inline-block", marginTop: "10px" }}>
                            <Button variant="success">+ {t.addProduct}</Button>
                        </Link>
                    </div>
                </Card.Body>
            </Card>

            {loading ? (
                <div className='table-responsive-sm'>Loading...</div>
            ) : (
                <>
                    <div className='table-responsive-sm'>
                        <table className="table table-striped table-responsive table-sm" style={{ fontSize: '14px', margin: 'auto' }}>
                            <thead className="table-dark text-center">
                                <tr>
                                    <th>#</th>
                                    <th>{t.productName}</th>
                                    <th>{t.category}</th>
                                    <th>{t.price}</th>
                                    <th>{t.costPrice}</th>
                                    <th>{t.stock}</th>
                                    <th>{t.barcode}</th>
                                    <th>{t.suppliers}</th>
                                    <th>{t.actions}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.map((product, index) => (
                                    <tr key={product.id} style={{ textAlign: 'center' }}>
                                        <td>{index + 1 + (currentPage - 1) * 20}</td>
                                        <td>{product.name}</td>
                                        <td>{product.category_name}</td>
                                        <td>Ksh {parseFloat(product.price).toFixed(2)}</td>
                                        <td>Ksh {parseFloat(product.cost_price).toFixed(2)}</td>
                                        <td>
                                            {product.quantity_in_stock < lowThereshold ? (
                                                <span style={{ color: "red", marginLeft: "10px" }}>
                                                    <progress value={product.quantity_in_stock} max={100} style={{ width: "90%" }} />
                                                    {t.lowStock} {product.quantity_in_stock}
                                                </span>
                                            ) : (
                                                <span style={{ color: "green", marginLeft: "10px" }}>
                                                    <progress value={product.quantity_in_stock} max={100} style={{ width: "90%" }} />
                                                    {t.inStock}: {product.quantity_in_stock}
                                                </span>
                                            )}
                                        </td>
                                        <td>{product.barcode}</td>
                                        <td>{product.supplier_name}</td>
                                        <td>
                                            <button
                                                className="btn btn-outline-info btn-sm me-2"
                                                onClick={() => handleView(product)}
                                                title="View"
                                            >
                                                <FontAwesomeIcon icon={faEye} />
                                            </button>
                                            <button
                                                className="btn btn-outline-warning btn-sm me-2"
                                                onClick={() => handleEdit(product)}
                                                title="Edit"
                                            >
                                                <FontAwesomeIcon icon={faEdit} />
                                            </button>
                                            {/* Conditionally render the edit and delete buttons */}
                                            {userRole === 'admin' && (
                                                <>
                                                    <button
                                                        className="btn btn-outline-danger btn-sm"
                                                        onClick={() => handleDelete(product.id)}
                                                        title="Delete"
                                                    >
                                                        <FontAwesomeIcon icon={faTrashAlt} />
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <Pagination>
                        <Pagination.Prev
                            onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                        />
                        {Array.from({ length: totalPages }, (_, index) => (
                            <Pagination.Item
                                key={index + 1}
                                active={index + 1 === currentPage}
                                onClick={() => setCurrentPage(index + 1)}
                            >
                                {index + 1}
                            </Pagination.Item>
                        ))}
                        <Pagination.Next
                            onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        />
                    </Pagination>
                </>
            )}

            {/* Product Detail Modal */}
            <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{selectedProduct?.name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedProduct && (
                        <div>
                            {/* Display default image if no image is available */}
                            {selectedProduct.image ? (
                                <img
                                    src={selectedProduct.image}
                                    alt={selectedProduct.name}
                                    style={{ width: '100%', height: 'auto', marginBottom: '15px' }}
                                />
                            ) : (
                                <>
                                    <p> There is No image, for this Product!</p>
                                </>
                            )}
                            <p><strong>Description:</strong> {selectedProduct.description}</p>
                            <p><strong>Price:</strong> ksh {parseFloat(selectedProduct.price).toFixed(2)}</p>
                            <p><strong>Cost Price:</strong> ksh {parseFloat(selectedProduct.cost_price).toFixed(2)}</p>
                            <p><strong>Quantity in Stock:</strong> {selectedProduct.quantity_in_stock}</p>
                            <p><strong>Barcode:</strong> {selectedProduct.barcode}</p>
                            <p><strong>Unit of Measurement:</strong> {selectedProduct.unit_of_measurement}</p>
                            <p><strong>Category:</strong> {selectedProduct.category_name}</p>
                            <p><strong>Supplier:</strong> {selectedProduct.supplier_name}</p>
                            <p><strong>Status:</strong> {selectedProduct.status}</p>
                            <p><strong>Date Added:</strong> {selectedProduct.date_added}</p>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDetailModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>

            {/* Product Edit Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Product</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedProduct && (
                        <div>
                            <div className="mb-3">
                                <label className="form-label">Name</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={selectedProduct.name}
                                    onChange={(e) => setSelectedProduct({ ...selectedProduct, name: e.target.value })}
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Description</label>
                                <textarea
                                    className="form-control"
                                    value={selectedProduct.description}
                                    onChange={(e) => setSelectedProduct({ ...selectedProduct, description: e.target.value })}
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Price</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={selectedProduct.price}
                                    onChange={(e) => setSelectedProduct({ ...selectedProduct, price: e.target.value })}
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Cost Price</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={selectedProduct.cost_price}
                                    onChange={(e) => setSelectedProduct({ ...selectedProduct, cost_price: e.target.value })}
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Quantity in Stock</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={selectedProduct.quantity_in_stock}
                                    onChange={(e) => setSelectedProduct({ ...selectedProduct, quantity_in_stock: parseInt(e.target.value) })}
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Barcode</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={selectedProduct.barcode}
                                    onChange={(e) => setSelectedProduct({ ...selectedProduct, barcode: e.target.value })}
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Unit of Measurement</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={selectedProduct.unit_of_measurement}
                                    onChange={(e) => setSelectedProduct({ ...selectedProduct, unit_of_measurement: e.target.value })}
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Category</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={selectedProduct.category_name}
                                    onChange={(e) => setSelectedProduct({ ...selectedProduct, category_name: e.target.value })}
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Supplier</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={selectedProduct.supplier_name}
                                    onChange={(e) => setSelectedProduct({ ...selectedProduct, supplier_name: e.target.value })}
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Status</label>
                                <select
                                    className="form-select"
                                    value={selectedProduct.status}
                                    onChange={(e) => setSelectedProduct({ ...selectedProduct, status: e.target.value })}
                                >
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>Close</Button>
                    <Button variant="primary" onClick={updateProduct}>Save Changes</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}